import React, { Component } from "react";
import styled from "styled-components";
import { Forms, Screens } from "../Customise";
import {
    CustomiseStepActions,
    CustomiseStepButton,
    CustomiseStepCaption,
    CustomiseStepCard,
    CustomiseStepCardTitle,
    CustomiseStepContent,
    CustomiseStepDivider,
    CustomiseStepField,
    CustomiseStepFieldGrid,
    CustomiseStepHeader,
    CustomiseStepShell,
    CustomiseStepTitle,
    LottieLoadingIndicator,
    PortalCaption,
    PortalSelect,
    portalColors
} from "src/components";
import { CustomiseContext } from "../CustomiseContext";
import { PhoneContainer } from "./UploadLogo";
import ImageSlider from "../components/ImageSlider";
import pluralize from "pluralize";
import { capitalizeString, customNames } from "src/utils/helpers";
import { getAvailableScreens, getDefaultStartScreen } from "../components/ScreenUtils";
import { AppScreens } from "../components/ScreenUtils";

const ButtonsContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 0.75rem;
`;

const StartScreenSelect = styled(PortalSelect)`
    width: 100%;
    min-width: 0;
`;

const InputLabel = styled.label`
    font-family: Manrope, Helvetica, sans-serif;
    font-weight: 700;
    font-size: 0.875rem;
    color: ${portalColors.text};
`;

const InputDescription = styled.p`
    font-family: Manrope, Helvetica, sans-serif;
    font-weight: 500;
    font-size: 0.8125rem;
    color: ${portalColors.mutedText};
    margin: 0;
`;


class StartScreen extends Component {
    static contextType = CustomiseContext;

    phoneSlideForAppScreen = new Map([
        [AppScreens.QUOTES, Screens.MAIN],
        [AppScreens.NOTICEBOARD, Screens.NOTICEBOARD],
        [AppScreens.BODY_AND_MIND, Screens.BODY_AND_MIND],
        [AppScreens.PLAYLISTS, Screens.PLAYLISTS],
        [AppScreens.GOALS, Screens.GOALS],
        [AppScreens.HABITS, Screens.GOALS]
    ]);

    clickNext = () => {
        if (this.context.isNextClickable) {
            this.context.nextClick();
        }
    };

    handleScreenChange = (userStartScreenType, e) => {
        const { setCurrentScreen } = this.context;
        this.context.updateUserStartScreen(userStartScreenType, e.target.value);
        const sliderScreen = this.phoneSlideForAppScreen.get(e.target.value);
        if (typeof sliderScreen === "number") {
            setCurrentScreen(sliderScreen);
        }
    };

    render() {
        const {
            companyName,
            currentScreen,
            currentForm,
            screenIds,
            leftScreen,
            rightScreen,
            colourPalette,
            assetLogoUrl,
            currentLogoUrl
        } = this.context;

        const featureFlags = this.context?.featureFlags || [];
        const availableScreens = getAvailableScreens(featureFlags);
        const defaultScreen = getDefaultStartScreen(featureFlags);

        return (
            <React.Fragment>
                <CustomiseStepShell $compact>
                    <CustomiseStepContent $compact>
                        <CustomiseStepHeader>
                            <div>
                                <CustomiseStepTitle>Step 4: Set Your App&rsquo;s Starting Screen (Optional)</CustomiseStepTitle>
                                <CustomiseStepCaption margin="0.25rem 0 0">
                                    Customise the default screen that {pluralize(customNames.employees.toLowerCase())} and {pluralize(customNames.clients.toLowerCase())} see when they open your app.
                                </CustomiseStepCaption>
                            </div>
                        </CustomiseStepHeader>

                        <CustomiseStepCard>
                            <CustomiseStepCardTitle>Selection Options</CustomiseStepCardTitle>
                            <CustomiseStepFieldGrid columns="1fr">
                                <CustomiseStepField>
                                    <InputLabel>
                                        {capitalizeString(pluralize(customNames.employees))}
                                    </InputLabel>
                                    <InputDescription>
                                        Select what {pluralize(customNames.employees.toLowerCase())} will see when they first open the app.
                                    </InputDescription>
                                    <StartScreenSelect
                                        disabled={this.context.isFinishing}
                                        value={this.context.employeeStartScreen}
                                        onChange={(e) => this.handleScreenChange("employeeStartScreen", e)}
                                        background={portalColors.surface}
                                    >
                                        {availableScreens.map((screen) => (
                                            <option key={screen} value={screen}>
                                                {screen === defaultScreen ? `${screen} (Default)` : screen}
                                            </option>
                                        ))}
                                    </StartScreenSelect>
                                </CustomiseStepField>

                                <CustomiseStepField>
                                    <InputLabel>
                                        {capitalizeString(pluralize(customNames.clients))}
                                    </InputLabel>
                                    <InputDescription>
                                        Select what {pluralize(customNames.clients.toLowerCase())} will see when they first open the app.
                                    </InputDescription>
                                    <StartScreenSelect
                                        disabled={this.context.isFinishing}
                                        value={this.context.clientStartScreen}
                                        onChange={(e) => this.handleScreenChange("clientStartScreen", e)}
                                        background={portalColors.surface}
                                    >
                                        {availableScreens.map((screen) => (
                                            <option key={screen} value={screen}>
                                                {screen === defaultScreen ? `${screen} (Default)` : screen}
                                            </option>
                                        ))}
                                    </StartScreenSelect>
                                </CustomiseStepField>
                            </CustomiseStepFieldGrid>
                        </CustomiseStepCard>

                        <CustomiseStepDivider />

                        <PortalCaption margin="0">
                            Click <b>Finish</b> to complete customisation. You can make additional changes as required by selecting <b>My Me App</b> on the side menu.
                        </PortalCaption>

                        <CustomiseStepActions>
                            <ButtonsContainer>
                                <CustomiseStepButton
                                    disabled={this.context.isFinishing}
                                    onClick={() => this.context.previousClick(Forms.ICON, Screens.MAIN)}
                                >
                                    Back
                                </CustomiseStepButton>

                                {this.context.isFinishing &&
                                    <div style={{ width: "112px" }}>
                                        <LottieLoadingIndicator
                                            containerHeight="0rem"
                                            height="2em"
                                            width="2em"
                                            margin="5px 0 0 0"
                                        />
                                    </div>
                                }
                                

                                { !this.context.isFinishing &&
                                    <CustomiseStepButton
                                        $variant="primary"
                                        onClick={this.clickNext}
                                    >
                                        Finish
                                    </CustomiseStepButton>
                                }
                            </ButtonsContainer>
                        </CustomiseStepActions>
                    </CustomiseStepContent>

                    <PhoneContainer>
                        <ImageSlider
                            companyName={companyName}
                            logoTopOffset={45}
                            phoneFrameTopOffset={30}
                            currentScreen={currentScreen}
                            currentForm={currentForm}
                            screenIds={screenIds}
                            leftScreen={leftScreen}
                            rightScreen={rightScreen}
                            colourPalette={colourPalette}
                            assetLogoUrl={currentLogoUrl || assetLogoUrl}
                        />
                    </PhoneContainer>
                </CustomiseStepShell>
            </React.Fragment>
        );
    }
}

export default StartScreen;
